import { formatDate, formatAmount } from './global';

// Using global jsPDF from CDN
const { jsPDF } = window.jspdf;

// Helper function to add new page if needed
const addNewPageIfNeeded = (doc, currentY, minSpaceNeeded) => {
  const pageHeight = doc.internal.pageSize.height;
  if (currentY + minSpaceNeeded > pageHeight - 20) {
    doc.addPage();
    return 30;
  }
  return currentY;
};

// Helper function to draw a grid cell
const drawGridCell = (doc, x, y, width, height, label, value, isLabel = false) => {
  // Draw cell background
  if (isLabel) {
    doc.setFillColor(249, 250, 251);
  } else {
    doc.setFillColor(255, 255, 255);
  }
  doc.rect(x, y, width, height, 'F');
  
  // Draw cell border
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.3);
  doc.rect(x, y, width, height, 'S');
  
  // Draw label
  if (label) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text(label, x + 3, y + 6);
  }
  
  // Draw value
  if (value) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(17, 24, 39);
    const lines = doc.splitTextToSize(String(value), width - 6);
    doc.text(lines, x + 3, y + (label ? 13 : 8));
  }
};

// Helper function to draw a 3-column grid row
const drawGridRow3Col = (doc, y, data, colWidths, rowHeight) => {
  const margin = 20;
  let xPos = margin;
  
  data.forEach((cell, index) => {
    drawGridCell(doc, xPos, y, colWidths[index], rowHeight, cell.label, cell.value);
    xPos += colWidths[index];
  });
  
  return y + rowHeight;
};

// Helper function to extract text segments with formatting from a node
const extractFormattedSegments = (node) => {
  const segments = [];
  
  const processNode = (n, inheritedBold = false, inheritedItalic = false) => {
    if (n.nodeType === Node.TEXT_NODE) {
      const text = n.textContent;
      if (text.trim()) {
        segments.push({
          text: text,
          bold: inheritedBold,
          italic: inheritedItalic
        });
      }
    } else if (n.nodeType === Node.ELEMENT_NODE) {
      const tagName = n.tagName.toLowerCase();
      let isBold = inheritedBold;
      let isItalic = inheritedItalic;
      
      if (tagName === 'strong' || tagName === 'b') {
        isBold = true;
      } else if (tagName === 'em' || tagName === 'i') {
        isItalic = true;
      } else if (tagName === 'u') {
        // Underline - jsPDF doesn't support underline directly, but we can note it
        // For now, we'll just process children
      }
      
      // Process all child nodes
      Array.from(n.childNodes).forEach(child => {
        processNode(child, isBold, isItalic);
      });
    }
  };
  
  processNode(node);
  return segments;
};

// Helper function to convert HTML to plain text and parse structure
const parseHtmlContent = (html) => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const result = [];
  
  const processNode = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      if (tagName === 'p') {
        const segments = extractFormattedSegments(node);
        if (segments.length > 0) {
          result.push({ type: 'paragraph', segments: segments });
        } else {
          result.push({ type: 'break' });
        }
      } else if (tagName === 'ul' || tagName === 'ol') {
        const list = { type: 'list', items: [] };
        const listItems = node.querySelectorAll('li');
        listItems.forEach(li => {
          const segments = extractFormattedSegments(li);
          if (segments.length > 0) {
            list.items.push(segments);
          }
        });
        if (list.items.length > 0) result.push(list);
      } else if (tagName === 'br') {
        result.push({ type: 'break' });
      } else if (tagName === 'strong' || tagName === 'b' || tagName === 'em' || tagName === 'i') {
        const segments = extractFormattedSegments(node);
        if (segments.length > 0) {
          result.push({ type: 'text', segments: segments });
        }
      } else {
        // Process children of other elements
        Array.from(node.childNodes).forEach(processNode);
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text && node.parentElement && node.parentElement.tagName.toLowerCase() !== 'p') {
        result.push({ type: 'text', segments: [{ text: text, bold: false, italic: false }] });
      }
    }
  };
  
  Array.from(tempDiv.childNodes).forEach(processNode);
  return result;
};

// Helper function to render custom letter content (HTML or plain text)
const renderCustomLetterContent = (doc, content, margin, contentWidth, startY) => {
  let currentY = startY;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  
  // Check if content is HTML (contains HTML tags)
  const isHtml = /<[a-z][\s\S]*>/i.test(content);
  
  if (isHtml) {
    // Parse HTML content
    const parsed = parseHtmlContent(content);
    
    // Helper function to render formatted text segments with proper wrapping and inline formatting
    const renderFormattedText = (segments, x, y, maxWidth) => {
      let currentY = y;
      const lineHeight = 5;
      let currentX = x;
      
      // Process segments and render with proper formatting
      segments.forEach((segment, segIndex) => {
        // Set font style based on formatting
        if (segment.bold && segment.italic) {
          doc.setFont('helvetica', 'bolditalic');
        } else if (segment.bold) {
          doc.setFont('helvetica', 'bold');
        } else if (segment.italic) {
          doc.setFont('helvetica', 'italic');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        
        const segmentText = segment.text.trim();
        if (!segmentText) return;
        
        // Calculate available width for this segment
        const availableWidth = maxWidth - (currentX - x);
        
        // Split segment text to fit available width
        const lines = doc.splitTextToSize(segmentText, availableWidth);
        
        lines.forEach((line, lineIndex) => {
          // If not first line of segment, start new line
          if (lineIndex > 0) {
            currentX = x;
            currentY += lineHeight;
            currentY = addNewPageIfNeeded(doc, currentY, lineHeight);
          }
          
          // Add space before segment if not first segment and not at start of line
          if (segIndex > 0 && currentX === x && lineIndex === 0) {
            // This is the first line of a new segment, but we're at start of line
            // Don't add space
          } else if (segIndex > 0 && lineIndex === 0 && currentX > x) {
            // Add space before segment if we're continuing on same line
            doc.setFont('helvetica', 'normal');
            doc.text(' ', currentX, currentY);
            currentX += doc.getTextWidth(' ');
            
            // Set font back for the segment
            if (segment.bold && segment.italic) {
              doc.setFont('helvetica', 'bolditalic');
            } else if (segment.bold) {
              doc.setFont('helvetica', 'bold');
            } else if (segment.italic) {
              doc.setFont('helvetica', 'italic');
            } else {
              doc.setFont('helvetica', 'normal');
            }
          }
          
          // Render the line
          doc.text(line, currentX, currentY);
          currentX += doc.getTextWidth(line);
        });
      });
      
      // Move to next line after all segments
      currentY += lineHeight;
      
      return currentY;
    };
    
    parsed.forEach((item) => {
      if (item.type === 'paragraph') {
        if (item.segments && item.segments.length > 0) {
          currentY = addNewPageIfNeeded(doc, currentY, 10);
          const startY = currentY;
          currentY = renderFormattedText(item.segments, margin, currentY, contentWidth);
          currentY += 3; // Space after paragraph
        } else {
          currentY += 5; // Empty paragraph
        }
      } else if (item.type === 'list') {
        item.items.forEach(listItemSegments => {
          currentY = addNewPageIfNeeded(doc, currentY, 10);
          doc.circle(margin + 2, currentY - 2, 1, 'F');
          const startY = currentY;
          currentY = renderFormattedText(listItemSegments, margin + 7, currentY, contentWidth - 10);
          currentY += 2;
        });
        currentY += 3; // Extra space after list
      } else if (item.type === 'text') {
        if (item.segments && item.segments.length > 0) {
          currentY = addNewPageIfNeeded(doc, currentY, 10);
          currentY = renderFormattedText(item.segments, margin, currentY, contentWidth);
          currentY += 3;
        }
      } else if (item.type === 'break') {
        currentY += 5;
      }
    });
  } else {
    // Handle plain text (backward compatibility)
    const lines = content.split('\n');
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      
      // Handle empty lines
      if (!trimmedLine) {
        currentY += 5;
        return;
      }
      
      // Handle bullet points (lines starting with •)
      if (trimmedLine.startsWith('•')) {
        const bulletText = trimmedLine.substring(1).trim();
        currentY = addNewPageIfNeeded(doc, currentY, 10);
        doc.circle(margin + 2, currentY - 2, 1, 'F');
        const textLines = doc.splitTextToSize(bulletText, contentWidth - 10);
        doc.text(textLines, margin + 7, currentY);
        currentY += (textLines.length * 5) + 2;
      } else {
        // Regular paragraph text
        const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
        currentY = addNewPageIfNeeded(doc, currentY, textLines.length * 5 + 5);
        doc.text(textLines, margin, currentY);
        currentY += (textLines.length * 5) + 3;
      }
    });
  }
  
  return currentY;
};

/**
 * @param {object} caseData
 * @param {string|null} customLetterContent
 * @param {{ disposition?: 'download' | 'blob' }} [options] disposition 'blob' returns { blob, filename } without saving; default downloads file.
 */
const generateChargebackPdf = (caseData, customLetterContent = null, options = {}) => {
  const disposition = options?.disposition === 'blob' ? 'blob' : 'download';
  const propertyLogoDataUrl = options?.propertyLogoDataUrl || '';
  const selectedPropertyName = options?.selectedPropertyName || '';
  try {
    const doc = new jsPDF();
    const margin = 20;
    const pageWidth = doc.internal.pageSize.width;
    const contentWidth = pageWidth - (margin * 2);
    let currentY = 20;
    
    // Header
    doc.setFillColor(34, 197, 94); // Green background
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text('Chargeback Dispute Filing Letter', margin, 15);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, 25);

    if (propertyLogoDataUrl) {
      try {
        doc.addImage(propertyLogoDataUrl, 'PNG', pageWidth - margin - 24, 8, 24, 24);
      } catch (e) {
        try {
          doc.addImage(propertyLogoDataUrl, 'JPEG', pageWidth - margin - 24, 8, 24, 24);
        } catch (innerError) {
          console.warn('Failed to render property logo in PDF:', innerError);
        }
      }
    }
    
    currentY = 50;
    
    // Title section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.text('Chargeback Dispute Filing Letter', margin, currentY);
    currentY += 12;
    
    // Draw horizontal line
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    if (selectedPropertyName) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      doc.text(`Property: ${selectedPropertyName}`, margin, currentY);
      currentY += 8;
    }
    
    // Grid layout for case information
    const colWidth = contentWidth / 3;
    const rowHeight = 18;
    
    // Row 1: Case Number, Status, Processed Date
    const row1 = [
      { label: 'Case Number', value: caseData.CASE_NO || 'N/A' },
      { label: 'Status', value: caseData.CHARGEBACK_STATUS || caseData.CHARGEBACK_WIN_LOSS || 'N/A' },
      { label: 'Processed Date', value: formatDate(caseData.TRANSACTION_DATE) || 'N/A' }
    ];
    currentY = drawGridRow3Col(doc, currentY, row1, [colWidth, colWidth, colWidth], rowHeight);
    
    // Row 2: Merchant ID, Merchant Name, Transaction Amount
    const row2 = [
      { label: 'Merchant ID', value: caseData.MERCHANT_ID || 'N/A' },
      { label: 'Merchant Name', value: caseData.MERCHANT_NAME || 'N/A' },
      { label: 'Transaction Amount', value: formatAmount(caseData.TRANSACTION_AMOUNT) || 'N/A' }
    ];
    currentY = drawGridRow3Col(doc, currentY, row2, [colWidth, colWidth, colWidth], rowHeight);
    
    currentY += 15;
    
    // Letter content section
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;
    
    // Use custom letter content if provided, otherwise use default
    if (customLetterContent) {
      currentY = renderCustomLetterContent(doc, customLetterContent, margin, contentWidth, currentY);
    } else {
      // Default letter content (original hardcoded version)
      // Letter salutation
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(17, 24, 39);
      doc.text('To Whom It May Concern,', margin, currentY);
      currentY += 10;
      
      // Letter body
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      
      const paragraphs = [
        'This letter serves as formal notification of our dispute regarding the chargeback for the above-referenced transaction.',
        '',
        'We believe this chargeback is invalid for the following reasons:',
      ];
      
      paragraphs.forEach(para => {
        if (para === '') {
          currentY += 5;
        } else {
          const lines = doc.splitTextToSize(para, contentWidth);
          currentY = addNewPageIfNeeded(doc, currentY, lines.length * 5 + 5);
          doc.text(lines, margin, currentY);
          currentY += (lines.length * 5) + 3;
        }
      });
      
      // Bullet points
      const bulletPoints = [
        'The transaction was properly authorized and completed.',
        'The customer received the goods/services as described.',
        'All terms of service were clearly communicated and agreed upon.'
      ];
      
      currentY += 3;
      bulletPoints.forEach(point => {
        currentY = addNewPageIfNeeded(doc, currentY, 10);
        doc.circle(margin + 2, currentY - 2, 1, 'F');
        const lines = doc.splitTextToSize(point, contentWidth - 10);
        doc.text(lines, margin + 7, currentY);
        currentY += (lines.length * 5) + 2;
      });
      
      currentY += 5;
      
      // Second paragraph
      const para2 = 'We have attached all relevant documentation to support our position, including:';
      const lines2 = doc.splitTextToSize(para2, contentWidth);
      currentY = addNewPageIfNeeded(doc, currentY, lines2.length * 5 + 5);
      doc.text(lines2, margin, currentY);
      currentY += (lines2.length * 5) + 5;
      
      // Second set of bullets
      const bulletPoints2 = [
        'Proof of delivery/service',
        'Transaction authorization records',
        'Customer communication history',
        'Any additional supporting documents'
      ];
      
      bulletPoints2.forEach(point => {
        currentY = addNewPageIfNeeded(doc, currentY, 10);
        doc.circle(margin + 2, currentY - 2, 1, 'F');
        doc.text(point, margin + 7, currentY);
        currentY += 7;
      });
      
      currentY += 5;
      
      // Closing paragraph
      const closingPara = 'We respectfully request that this chargeback be reversed and the funds be returned to our account. Please let us know if you require any additional information to process this dispute.';
      const closingLines = doc.splitTextToSize(closingPara, contentWidth);
      currentY = addNewPageIfNeeded(doc, currentY, closingLines.length * 5 + 20);
      doc.text(closingLines, margin, currentY);
      currentY += (closingLines.length * 5) + 15;
      
      // Signature
      doc.setFont('helvetica', 'normal');
      doc.text('Sincerely,', margin, currentY);
      currentY += 7;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Your Name', margin, currentY);
      currentY += 7;
      
      doc.setFont('helvetica', 'normal');
      doc.text('Dispute Resolution Department', margin, currentY);
    }
    
    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    const formattedDate = new Date().toLocaleString();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'normal');
      
      const footerY = doc.internal.pageSize.height - 10;
      
      // Left: Document number
      doc.text(`Document #${caseData.CASE_NO || 'XXXXX'}`, margin, footerY);
      
      // Center: Page number
      const pageText = `Page ${i} of ${pageCount}`;
      const pageTextWidth = doc.getTextWidth(pageText);
      doc.text(pageText, (pageWidth - pageTextWidth) / 2, footerY);
      
      // Right: Generated date
      const dateText = `Generated: ${formattedDate}`;
      const dateTextWidth = doc.getTextWidth(dateText);
      doc.text(dateText, pageWidth - margin - dateTextWidth, footerY);
    }
    
    const filename = `Chargeback_${caseData.CASE_NO || 'case'}_${new Date().toISOString().split('T')[0]}.pdf`;

    if (disposition === 'blob') {
      const blob = doc.output('blob');
      return { blob, filename };
    }

    doc.save(filename);
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export default generateChargebackPdf;